import type { MoveToCommand } from '@/lib/elements/types/move-to-command'
import type { PathCommand } from '@/lib/elements/types/path-command'

export function svgPathToPathCommands(d: string): PathCommand[] {
  const tokens = d.trim().match(/[MmLlCcQqZz]|[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?/g) ?? []
  const commands: PathCommand[] = []
  let i = 0

  const nextNum = (): number => parseFloat(tokens[++i] ?? '0')

  while (i < tokens.length) {
    const cmd = tokens[i]

    switch (cmd) {
      case 'M':
      case 'm':
        commands.push({ moveTo: { x: nextNum(), y: nextNum() } })
        i++
        break
      case 'L':
      case 'l':
        commands.push({ lineTo: { x: nextNum(), y: nextNum() } })
        i++
        break
      case 'C':
      case 'c': {
        const controlX = nextNum()
        const controlY = nextNum()
        const controlX2 = nextNum()
        const controlY2 = nextNum()
        const x = nextNum()
        const y = nextNum()

        commands.push({ curveTo: { controlX, controlY, controlX2, controlY2, x, y } })
        i++
        break
      }

      case 'Q':
      case 'q': {
        // Quadratic bezier — TrueType glyph outlines from opentype.js emit these (this codebase's
        // own path generation never has). `curveTo` already models quadratic directly by omitting
        // controlX2/controlY2 (see the type comment above), so no cubic elevation is needed here —
        // `pathCommandsToSvgPath` re-emits this exact command as a `Q`.
        const controlX = nextNum()
        const controlY = nextNum()
        const x = nextNum()
        const y = nextNum()

        commands.push({ curveTo: { controlX, controlY, x, y } })
        i++
        break
      }

      case 'Z':
      case 'z': {
        // Encode the close explicitly so the closing segment survives the
        // pathCommands round-trip (pathCommandsToSvgPath never re-adds Z for
        // kindId=9 polylines). Only add the lineTo when the last command's
        // endpoint doesn't already return to the current subpath's own moveTo —
        // a compound path (multiple M's, e.g. a glyph outline with a hole, or
        // several disjoint glyphs merged into one path) closes each subpath back
        // to ITS OWN most recent moveTo, not the very first one in the array.
        let subpathStart: MoveToCommand | null = null

        for (let j = commands.length - 1; j >= 0; j--) {
          const c = commands[j]

          if ('moveTo' in c) {
            subpathStart = c
            break
          }
        }

        if (subpathStart) {
          const fx = subpathStart.moveTo.x
          const fy = subpathStart.moveTo.y
          const last = commands[commands.length - 1]
          let lastX = fx
          let lastY = fy

          if ('lineTo' in last) {
            lastX = last.lineTo.x
            lastY = last.lineTo.y
          } else if ('curveTo' in last) {
            lastX = last.curveTo.x
            lastY = last.curveTo.y
          }

          if (Math.abs(lastX - fx) > 0.01 || Math.abs(lastY - fy) > 0.01) {
            commands.push({ lineTo: { x: fx, y: fy } })
          }
        }

        i++
        break
      }

      default:
        i++
    }
  }

  return commands
}

export function pathCommandsToSvgPath(
  commands: PathCommand[],
  options?: { close?: boolean }
): string {
  const parts: string[] = []
  let sawMoveTo = false

  for (const cmd of commands) {
    if ('moveTo' in cmd) {
      // A compound path (multiple M's — e.g. a glyph outline with a hole, or several disjoint
      // glyphs merged into one path) closes each earlier subpath before starting the next one;
      // `options.close` only governs the FINAL subpath, matching this function's pre-existing
      // single-subpath behavior (a pen-tool polyline can be deliberately left open).
      if (sawMoveTo) parts.push('Z')
      sawMoveTo = true
      parts.push(`M ${cmd.moveTo.x} ${cmd.moveTo.y}`)
    } else if ('lineTo' in cmd) {
      parts.push(`L ${cmd.lineTo.x} ${cmd.lineTo.y}`)
    } else if ('curveTo' in cmd) {
      const c2x = cmd.curveTo.controlX2 ?? cmd.curveTo.deltaX
      const c2y = cmd.curveTo.controlY2 ?? cmd.curveTo.deltaY

      if (typeof c2x === 'number' && typeof c2y === 'number') {
        parts.push(
          `C ${cmd.curveTo.controlX} ${cmd.curveTo.controlY} ${c2x} ${c2y} ${cmd.curveTo.x} ${cmd.curveTo.y}`
        )
      } else {
        parts.push(
          `Q ${cmd.curveTo.controlX} ${cmd.curveTo.controlY} ${cmd.curveTo.x} ${cmd.curveTo.y}`
        )
      }
    }
  }

  if (options?.close ?? true) {
    parts.push('Z')
  }

  return parts.join(' ')
}
